import React, { useState, useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
// import DateInput from "src/_common/components/form-elements/datepicker/dateInput";
// import TimePicker from "src/_common/components/form-elements/timePicker/timePicker";
import { useUsersManagementApi } from "src/_common/hooks/actions";
import { useInterviewApi } from "src/_common/hooks/actions/interview/appInterviewApiHook";
import { useToaster } from "src/_common/hooks/actions/common/appToasterHook";
import { useTranslation } from "src/_common/hooks/useTranslation";
import moment from "moment";

interface Member {
  id: number;
  name: string;
  email: string;
  designation: string;
  profile_photo_url?: string;
  profile_image_url?: string;
  status: number;
  user_type: string;
  company_id: number;
}

interface ScheduleInterviewFormData {
  selectedMember: string;
  interviewDate: Date | null;
  interviewTime: Date | null;
  hours: number | string;
}

interface ScheduleInterviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  candidateName: string;
  candidateId: string;
  jobId: string;
  onSchedule: (data: ScheduleInterviewFormData) => void;
}

const ScheduleInterviewModal: React.FC<ScheduleInterviewModalProps> = ({
  isOpen,
  onClose,
  candidateName,
  candidateId,
  jobId,
  onSchedule,
}) => {
  const { t } = useTranslation("job-details");
  const [members, setMembers] = useState<Member[]>([]);
  const [isLoadingMembers, setIsLoadingMembers] = useState(false);
  const [isScheduling, setIsScheduling] = useState(false);

  const { callGetUsers } = useUsersManagementApi();
  const { callScheduleRequest } = useInterviewApi();
  const toast = useToaster();

  // Validation schema
  const scheduleSchema = yup.object().shape({
    selectedMember: yup.string().required(t("jobDetails.interview.scheduleModal.validation.selectMember")),
  });

  const {
    control,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
    reset,
  } = useForm<ScheduleInterviewFormData>({
    resolver: yupResolver(scheduleSchema),
    defaultValues: {
      selectedMember: "",
      interviewDate: null,
      interviewTime: null,
      hours: "",
    },
  });

  // Watch selected member for UI updates
  const selectedMember = watch("selectedMember");

  // Load team members when modal opens
  useEffect(() => {
    if (isOpen) {
      fetchMembers();
      // Reset form when modal opens
      reset();
    }
  }, [isOpen, reset]);

  const fetchMembers = () => {
    setIsLoadingMembers(true);
    const params = {
      limit: 50, // Get more members for selection
      offset: 0,
    };

    callGetUsers(
      params,
      (message: string, data: any) => {
        console.log("Members loaded successfully:", data);
        if (data && data.members) {
          setMembers(data.members);
        }
        setIsLoadingMembers(false);
      },
      (error: string) => {
        console.error("Error loading members:", error);
        toast.error("Failed to load team members: " + error);
        setIsLoadingMembers(false);
      }
    );
  };

  const onSubmit = async (data: ScheduleInterviewFormData) => {
    try {
      setIsScheduling(true);
      console.log("Scheduling interview with data:", data);

      // Temporarily disabled date and time fields
      // const formattedDate = moment(data.interviewDate).format("YYYY-MM-DD");
      // const formattedTime = moment(data.interviewTime).format("HH:mm");
      // const interviewSchedule = `${formattedDate} ${formattedTime}`;

      // Prepare API payload according to user specifications
      const apiPayload = {
        job_id: jobId,
        member_id: data.selectedMember,
        // interview_schedule: interviewSchedule, // temporarily disabled
        // status: 0,
        title: "Interview Scheduled",
        description: "Interview has been scheduled with team member",
        screening_id: candidateId,
      };

      console.log("API Payload:", apiPayload);

      // Call schedule request directly (no validity update for manual interview)
      (callScheduleRequest as any)(
        apiPayload,
        (message: string, response: any) => {
          toast.success(message);

          // Call the parent onSchedule function for any additional handling
          onSchedule(data);

          // Reset loading state and close modal
          setIsScheduling(false);
          handleClose();
        },
        (error: string) => {
          console.error("Error scheduling interview:", error);
          toast.error("Failed to schedule interview: " + error);
          setIsScheduling(false);
        }
      );
    } catch (error) {
      console.error("Error scheduling interview:", error);
      toast.error(t("jobDetails.interview.scheduleModal.scheduleError"));
      setIsScheduling(false);
    }
  };

  const handleClose = () => {
    if (!isScheduling) {
      reset();
      onClose();
    }
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget && !isScheduling) {
      handleClose();
    }
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div className="currency-modal-overlay" onClick={handleOverlayClick}>
      <div className="currency-modal schedule-interview-modal" onClick={(e) => e.stopPropagation()}>
        <div className="currency-modal-header">
          <h3 className="currency-head">
            {t("jobDetails.interview.scheduleModal.title")} {candidateName}
          </h3>
          <button className="close-modal" onClick={handleClose} disabled={isScheduling}>
            ×
          </button>
        </div>

        <div className="ai-description-content">
          <form onSubmit={handleSubmit(onSubmit)}>
            {/* Team Member Selection */}
            <div className="drjob-form-group">
              <label className="form-label">
                {t("jobDetails.interview.scheduleModal.selectMember")} <span className="mandatory-mark">*</span>
              </label>
              <Controller
                name="selectedMember"
                control={control}
                render={({ onChange, onBlur, value }) => (
                  <select
                    className={`form-control ${errors.selectedMember ? "is-invalid" : ""}`}
                    value={value || ""}
                    onChange={(e) => onChange(e.target.value)}
                    onBlur={onBlur}
                    disabled={isLoadingMembers}
                  >
                    <option value="">
                      {isLoadingMembers
                        ? t("jobDetails.interview.scheduleModal.loadingMembers")
                        : t("jobDetails.interview.scheduleModal.selectPlaceholder")}
                    </option>

                    {/* Then add all members */}
                    {members
                      .filter((member) => member.name && member.name.trim())
                      .map((member) => (
                        <option key={member.id} value={member.id.toString()}>
                          {member.designation ? `${member.name} - ${member.designation}` : member.name}
                        </option>
                      ))}
                  </select>
                )}
              />
              {errors.selectedMember && <span className="error-message">{errors.selectedMember.message}</span>}
            </div>

            {/* Interview Date - temporarily disabled */}
            {/**
            <div className="drjob-form-group">
              <label className="form-label">
                Interview Date <span className="mandatory-mark">*</span>
              </label>
              <Controller
                name="interviewDate"
                control={control}
                render={({ onChange, onBlur, value }) => (
                  <DateInput
                    onChange={onChange}
                    onBlur={onBlur}
                    value={value}
                    placeholder="Select interview date"
                    error={errors.interviewDate}
                    minDate={new Date()}
                    dateFormat="MM/dd/yyyy"
                  />
                )}
              />
            </div>
            **/}

            {/* Interview Time - temporarily disabled */}
            {/**
            <div className="drjob-form-group">
              <label className="form-label">
                Interview Time <span className="mandatory-mark">*</span>
              </label>
              <Controller
                name="interviewTime"
                control={control}
                render={({ onChange, onBlur, value }) => (
                  <TimePicker
                    onChange={onChange}
                    onBlur={onBlur}
                    value={value}
                    placeholder="Select interview time"
                    error={errors.interviewTime}
                    timeIntervals={30}
                  />
                )}
              />
            </div>
            **/}

            {/* Action Buttons */}
            <div className="modal-actions">
              <button type="button" className="btn-cancel" onClick={handleClose} disabled={isScheduling}>
                {t("jobDetails.interview.scheduleModal.cancel")}
              </button>
              <button type="submit" className="btn-preview" disabled={isScheduling}>
                {isScheduling ? (
                  <>{t("jobDetails.interview.scheduleModal.scheduling")}</>
                ) : (
                  <>{t("jobDetails.interview.scheduleModal.scheduleButton")}</>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ScheduleInterviewModal;
